import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  PaymentStatus,
  PaymentVerificationStatus,
} from '@techspert-io/engagements';
import { ToastService } from '@techspert-io/user-alerts';
import { catchError, Observable, of } from 'rxjs';
import { IPayment } from '../models/payments.models';

export interface IPaymentQueryOptions {
  paymentStatuses: PaymentStatus[];
  verificationStatus: PaymentVerificationStatus;
}

export interface IMappedPayment extends IPayment {
  expertName: string;
  email: string;
  ownerName: string;
  actualPaymentCurrency: string;
}

export enum PaymentRecordType {
  Engagement = 'engagement',
  Payment = 'payment',
}

@Injectable({
  providedIn: 'root',
})
export class PaymentsService {
  private readonly baseUrl = '/payments';

  constructor(
    private http: HttpClient,
    private toastService: ToastService
  ) {}

  query({
    paymentStatuses,
    ...queryOptions
  }: Partial<IPaymentQueryOptions>): Observable<IMappedPayment[]> {
    if (!paymentStatuses?.length) {
      return of([]);
    }

    const params = Object.entries({
      ...queryOptions,
      status: paymentStatuses.join(','),
    })
      .filter(([, v]) => !!v)
      .reduce((acc, [k, v]) => acc.set(k, v), new HttpParams());

    return this.http
      .get<IMappedPayment[]>(`${this.baseUrl}/dashboard`, {
        params,
      })
      .pipe(
        catchError((err) => {
          this.showErrorToast(
            err instanceof HttpErrorResponse ? err.error.message : err.message
          );

          return of([]);
        })
      );
  }

  update(
    payment: Partial<IPayment> & { paymentId: string }
  ): Observable<IPayment> {
    return this.http.patch<IPayment>(
      `${this.baseUrl}/dashboard/payment/${payment.paymentId}`,
      payment
    );
  }

  markAsSent(payload: { paymentIds: string[]; type: PaymentRecordType }) {
    if (!payload.paymentIds.length) {
      return of([]);
    }

    return this.http.post<string[]>(`${this.baseUrl}/mark-sent`, payload).pipe(
      catchError((err) => {
        this.showErrorToast(
          err instanceof HttpErrorResponse ? err.error.message : err.message
        );

        return of([]);
      })
    );
  }

  private showErrorToast(
    message: string,
    errorLevel: 'error' | 'danger' = 'danger'
  ) {
    this.toastService.sendMessage(message, errorLevel);
  }
}
